import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { toast } from 'sonner';

import {
  EmptyState,
  ListRow,
  ListShell,
  ReadOnlyNotice,
  SectionCard,
  SettingsSkeleton,
} from '@/components/settings/settings-kit';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Field, FieldDescription, FieldError, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { useAuth } from '@/lib/auth';
import {
  addCustomDomain,
  checkDomain,
  claimSubdomain,
  type DomainRecord,
  deleteDomain,
  listDomains,
  rootDomain,
  setPrimaryDomain,
  subdomainAvailable,
} from '@/lib/settings/storefront';

/**
 * Where the storefront answers.
 *
 * Every store gets one `*.cannactrl.app` subdomain, claimed atomically
 * against `reserved_subdomains` so two merchants cannot race for the same
 * name. Custom domains are recorded here with the DNS the merchant has to
 * publish; issuing the certificate is Cloudflare for SaaS's job at deploy
 * time, which is why a fresh row sits at `pending` rather than lying about
 * being live.
 */

const SUBDOMAIN = /^[a-z0-9]([a-z0-9-]{1,28}[a-z0-9])?$/;
const HOSTNAME = /^([a-z0-9-]+\.)+[a-z]{2,}$/;

/**
 * The database raises its refusals prefixed with the function that made them
 * ("add_custom_domain: a paid plan is required…"). The rule is the operator's
 * business; the function name is not.
 */
function readable(message: string) {
  return message.replace(/^[a-z_]+:\s*/i, '');
}

const STATUS_LABELS: Record<string, string> = {
  pending: 'Waiting for DNS',
  verifying: 'Checking DNS',
  active: 'Live',
  failed: 'Failed',
  removed: 'Removed',
};

export function DomainSettings({ accountId }: { accountId: string }) {
  const { can } = useAuth();
  const canManage = can('settings.manage');
  const queryClient = useQueryClient();

  const [subdomain, setSubdomain] = useState('');
  const [subdomainError, setSubdomainError] = useState<string | null>(null);
  const [custom, setCustom] = useState('');
  const [customError, setCustomError] = useState<string | null>(null);

  const domains = useQuery({
    queryKey: ['storefront-domains', accountId],
    queryFn: () => listDomains(accountId),
  });

  const root = useQuery({ queryKey: ['storefront-root-domain'], queryFn: rootDomain });

  const refresh = () =>
    queryClient.invalidateQueries({ queryKey: ['storefront-domains', accountId] });

  const claim = useMutation({
    mutationFn: async () => {
      const candidate = subdomain.trim().toLowerCase();

      if (!SUBDOMAIN.test(candidate)) {
        throw new Error('Use 3-30 letters, numbers and dashes, starting and ending with one.');
      }

      // Asked before claiming so the answer is "taken", not a constraint.
      if (!(await subdomainAvailable(candidate))) {
        throw new Error('That name is taken or reserved. Try another.');
      }

      await claimSubdomain(accountId, candidate);
    },
    onSuccess: () => {
      setSubdomain('');
      setSubdomainError(null);
      toast.success('Subdomain claimed.');
      refresh();
    },
    onError: (error: Error) => setSubdomainError(readable(error.message)),
  });

  const add = useMutation({
    mutationFn: async () => {
      const hostname = custom
        .trim()
        .toLowerCase()
        .replace(/^https?:\/\//, '')
        .replace(/\/.*$/, '');

      if (!HOSTNAME.test(hostname)) throw new Error('Enter a domain like shop.example.com.');

      await addCustomDomain(accountId, hostname);
    },
    onSuccess: () => {
      setCustom('');
      setCustomError(null);
      toast.success('Domain added. Publish the DNS records below.');
      refresh();
    },
    onError: (error: Error) => setCustomError(readable(error.message)),
  });

  const makePrimary = useMutation({
    mutationFn: (id: string) => setPrimaryDomain(accountId, id),
    onSuccess: () => {
      toast.success('Primary domain changed.');
      refresh();
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const check = useMutation({
    mutationFn: (id: string) => checkDomain(accountId, id),
    onSuccess: (result) => {
      toast.success(
        result.status === 'active'
          ? 'Verified. The domain is live.'
          : 'Not verified yet. Cloudflare has not seen the DNS records.',
      );
      refresh();
    },
    onError: (error: Error) => toast.error(readable(error.message)),
  });

  const remove = useMutation({
    mutationFn: (id: string) => deleteDomain(accountId, id),
    onSuccess: () => {
      toast.success('Domain removed.');
      refresh();
    },
    onError: (error: Error) => toast.error(error.message),
  });

  if (domains.isLoading) return <SettingsSkeleton />;

  const hasSubdomain = domains.data?.some((domain) => domain.kind === 'subdomain');

  return (
    <div className="flex max-w-3xl flex-col gap-6">
      {!canManage && <ReadOnlyNotice permission="settings.manage" />}

      <SectionCard
        title="Addresses"
        description="The primary one is what links and receipts use; the others redirect to it."
      >
        {domains.data?.length ? (
          <ListShell>
            {domains.data.map((domain) => (
              <ListRow
                key={domain.id}
                title={domain.hostname}
                subtitle={domain.error ?? STATUS_LABELS[domain.status] ?? domain.status}
                badges={
                  <>
                    {domain.isPrimary && <Badge>Primary</Badge>}
                    <Badge variant="secondary">{domain.kind}</Badge>
                  </>
                }
                actions={
                  canManage ? (
                    <div className="flex items-center gap-1">
                      {!domain.isPrimary && (
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          onClick={() => makePrimary.mutate(domain.id)}
                        >
                          Make primary
                        </Button>
                      )}
                      {domain.kind === 'custom' && domain.status !== 'active' && (
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          disabled={check.isPending}
                          onClick={() => check.mutate(domain.id)}
                        >
                          Check DNS
                        </Button>
                      )}
                      {domain.kind === 'custom' && (
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          onClick={() => remove.mutate(domain.id)}
                        >
                          Remove
                        </Button>
                      )}
                    </div>
                  ) : null
                }
              />
            ))}
          </ListShell>
        ) : (
          <EmptyState>
            No addresses yet, so the storefront is unreachable. Claim a subdomain to open it.
          </EmptyState>
        )}

        {domains.data
          ?.filter((domain) => domain.kind === 'custom' && domain.verificationRecords.length)
          .map((domain) => (
            <DnsRecords key={domain.id} domain={domain} />
          ))}
      </SectionCard>

      {canManage && (
        <>
          <SectionCard
            title="Your cannactrl address"
            description={
              hasSubdomain
                ? 'Already claimed. Claiming another adds it alongside the first.'
                : 'One free address, live the moment you claim it.'
            }
          >
            <Field data-invalid={!!subdomainError}>
              <FieldLabel htmlFor="subdomain">Subdomain</FieldLabel>
              <div className="flex items-center gap-2">
                <Input
                  id="subdomain"
                  value={subdomain}
                  placeholder="farmhouse"
                  onChange={(event) => {
                    setSubdomain(event.target.value);
                    setSubdomainError(null);
                  }}
                />
                <span className="text-muted-foreground text-sm whitespace-nowrap">
                  .{root.data ?? 'cannactrl.app'}
                </span>
                <Button
                  type="button"
                  size="sm"
                  disabled={!subdomain.trim() || claim.isPending}
                  onClick={() => claim.mutate()}
                >
                  Claim
                </Button>
              </div>
              {subdomainError && <FieldError>{subdomainError}</FieldError>}
            </Field>
          </SectionCard>

          <SectionCard
            title="Custom domain"
            description="Point your own domain here. Add it, publish the DNS records, and it goes live once they resolve."
          >
            <Field data-invalid={!!customError}>
              <FieldLabel htmlFor="custom-domain">Domain</FieldLabel>
              <div className="flex items-center gap-2">
                <Input
                  id="custom-domain"
                  value={custom}
                  placeholder="shop.example.com"
                  onChange={(event) => {
                    setCustom(event.target.value);
                    setCustomError(null);
                  }}
                />
                <Button
                  type="button"
                  size="sm"
                  disabled={!custom.trim() || add.isPending}
                  onClick={() => add.mutate()}
                >
                  Add
                </Button>
              </div>
              <FieldDescription>
                An apex domain (example.com) needs a CNAME-flattening or ALIAS record at your DNS
                host.
              </FieldDescription>
              {customError && <FieldError>{customError}</FieldError>}
            </Field>
          </SectionCard>
        </>
      )}
    </div>
  );
}

function DnsRecords({ domain }: { domain: DomainRecord }) {
  return (
    <div className="rounded-lg border border-dashed p-4">
      <p className="text-sm font-medium">DNS for {domain.hostname}</p>
      <p className="text-muted-foreground mt-1 text-xs">
        Publish every one of these at your DNS host, then choose Check DNS. Two certificate rows can
        share a name — that is expected, and both are needed, so add them as separate TXT records
        rather than replacing one with the other. Publish them before pointing the domain here: the
        certificate is issued first, so the switch happens with no downtime.
      </p>

      <table className="mt-3 w-full text-left text-xs">
        <thead className="text-muted-foreground">
          <tr>
            <th className="py-1 pr-3 font-medium">Proves</th>
            <th className="py-1 pr-3 font-medium">Type</th>
            <th className="py-1 pr-3 font-medium">Name</th>
            <th className="py-1 font-medium">Value</th>
          </tr>
        </thead>
        <tbody className="font-mono">
          {domain.verificationRecords.map((record) => (
            <tr key={`${record.type}-${record.name}-${record.value}`}>
              <td className="py-1 pr-3 font-sans">
                {record.kind === 'certificate' ? 'Certificate' : 'Ownership'}
              </td>
              <td className="py-1 pr-3">{(record.type ?? 'txt').toUpperCase()}</td>
              <td className="py-1 pr-3 break-all">{record.name ?? '@'}</td>
              <td className="py-1 break-all">{record.value ?? ''}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
